// src/api/ecritureEnPool.ts — SCE Issues
// Écritures en rafale vers une liste SharePoint : réessai sur 429 / 503 / 504
// et petit pool de 3. Sorti de equipementsImport.api le 06/10 pour l'import de
// la base FNC-T (fnct.api), qui écrit un millier de lignes.

import { GraphApiError } from './graphClient'

/**
 * Rejoue un appel Graph en cas de throttling (429) ou d'indisponibilité
 * temporaire (503/504), en respectant l'en-tête Retry-After quand Graph
 * le fournit — sinon backoff exponentiel.
 */
export async function avecReessai<T>(
  appel: () => Promise<T>,
  tentatives = 3,
): Promise<T> {
  let attente = 1000

  for (let essai = 1; ; essai += 1) {
    try {
      return await appel()
    } catch (err) {
      const recuperable =
        err instanceof GraphApiError &&
        (err.status === 429 || err.status === 503 || err.status === 504)

      if (!recuperable || essai >= tentatives) throw err

      await new Promise((resolve) => setTimeout(resolve, attente))
      attente *= 2
    }
  }
}

/**
 * Exécute des tâches avec un petit pool de workers.
 *
 * Concurrence volontairement basse (3) : Graph throttle agressivement les
 * écritures en rafale sur une même liste SharePoint, et un import mensuel
 * n'a pas besoin d'être plus rapide que ça.
 */
export async function executerEnPool<T>(
  taches: T[],
  traiter: (tache: T) => Promise<void>,
  onProgress: (faits: number) => void,
  concurrence = 3,
): Promise<void> {
  let curseur = 0
  let faits = 0

  async function worker(): Promise<void> {
    for (;;) {
      const index = curseur
      curseur += 1
      const tache = taches[index]
      if (tache === undefined) return

      await traiter(tache)
      faits += 1
      onProgress(faits)
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrence, taches.length) }, () => worker()),
  )
}
